import React, { useState, useEffect, useMemo } from 'react';
import './CategoryRecordsModal.css';
import { FaTimes, FaDownload, FaCalendarAlt, FaUser, FaPhoneAlt, FaUserMd, FaUserPlus, FaCheckSquare, FaSquare, FaExclamationTriangle, FaFileInvoiceDollar, FaFilter, FaExternalLinkAlt, FaCheckCircle } from 'react-icons/fa';
import axios from 'axios';
import { API_BASE_URL } from '../../../../Constants/ApiEndpoints';

const CategoryRecordsModal = ({ isOpen, onClose, categoryData, categoryType, fromDate, toDate, showDoctorAndAddedBy = true }) => {
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [patientFilter, setPatientFilter] = useState('');
  const [addedByFilter, setAddedByFilter] = useState('');
  const [showPatientFilter, setShowPatientFilter] = useState(false);
  const [showAddedByFilter, setShowAddedByFilter] = useState(false);

  const [liveRecords, setLiveRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch live backend itemized details when modal opens
  useEffect(() => {
    if (isOpen && categoryData) {
      const invName = categoryData.particulars || categoryData.invoiceName;
      if (!invName) return;

      setLoading(true);
      setErrorMessage('');
      const params = {
        invoiceName: invName
      };
      if (categoryType && categoryType !== 'all') {
        params.status = categoryType;
      }
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      axios.get(`${API_BASE_URL}/getPaymentCategoryDetails`, { params })
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          setLiveRecords(res.data);
        } else {
          setLiveRecords([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch live category records:", err);
        setLiveRecords([]);
        if (err.response && err.response.status === 404) {
          setErrorMessage('Backend endpoint not found. Please restart your Spring Boot backend server.');
        } else {
          setErrorMessage('Unable to connect to backend endpoint. Please restart your backend server.');
        }
      })
      .finally(() => {
        setLoading(false);
      });
    }
  }, [isOpen, categoryData, fromDate, toDate]);

  // Format title for clean enterprise look (e.g. INPATIENTCHARGES -> Inpatient Charges)
  const formatTitle = (rawTitle) => {
    if (!rawTitle) return "Category Breakdown Records";
    if (rawTitle.toLowerCase() === "inpatientcharges") return "Inpatient Charges";
    if (rawTitle.toLowerCase() === "opcharges") return "Outpatient Charges";
    return rawTitle
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const isCompletedStatus = (status) => {
    if (!status) return false;
    const s = String(status).toUpperCase();
    return s === "PAID" || s === "FREE" || s === "OVERPAID" || s === "FULLY_REFUNDED" || s === "COMPLETED";
  };

  // Apply column filters on LIVE backend records
  const filteredRecords = useMemo(() => {
    return liveRecords.filter((rec) => {
      const matchPatient = !patientFilter || (rec.patientName && rec.patientName.toLowerCase().includes(patientFilter.toLowerCase())) || (rec.mrNumber && rec.mrNumber.toLowerCase().includes(patientFilter.toLowerCase()));
      const matchAddedBy = !addedByFilter || (rec.addedBy && rec.addedBy.toLowerCase().includes(addedByFilter.toLowerCase()));
      return matchPatient && matchAddedBy;
    });
  }, [liveRecords, patientFilter, addedByFilter]);

  // Total amount
  const totalAmount = useMemo(() => {
    return filteredRecords.reduce((sum, r) => sum + (r.amount || 0), 0);
  }, [filteredRecords]);

  // Selected Amount
  const selectedAmount = useMemo(() => {
    return filteredRecords
      .filter((r) => selectedRowIds.includes(r.id))
      .reduce((sum, r) => sum + (r.amount || 0), 0);
  }, [filteredRecords, selectedRowIds]);

  if (!isOpen || !categoryData) return null;

  const isAllSelected = filteredRecords.length > 0 && selectedRowIds.length === filteredRecords.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(filteredRecords.map((r) => r.id));
    }
  };

  const toggleRowSelect = (id) => {
    if (selectedRowIds.includes(id)) {
      setSelectedRowIds(selectedRowIds.filter((rowId) => rowId !== id));
    } else {
      setSelectedRowIds([...selectedRowIds, id]);
    }
  };

  const handleCSVDownload = () => {
    const rowsToExport = selectedRowIds.length > 0
      ? filteredRecords.filter((r) => selectedRowIds.includes(r.id))
      : filteredRecords;

    const headers = ["Invoice ID", "Appointment ID", "Date", "Patient Name", "MR Number", "Phone", "Doctor", "Added By", "Amount (INR)"];
    const csvContent = [
      headers.join(","),
      ...rowsToExport.map(r => `"${r.id || ''}","${r.appointmentId || ''}","${r.date}","${r.patientName}","${r.mrNumber}","${r.phone}","${r.doctor}","${r.addedBy}",${r.amount}`)
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${categoryData.particulars || categoryData.invoiceName || 'Report'}_Details.csv`;
    link.click();
  };

  const rawTitle = categoryData.particulars || categoryData.invoiceName || "Payment Category Records";
  const displayTitle = formatTitle(rawTitle);

  return (
    <div className="cat-modal-backdrop" onClick={onClose}>
      <div className="cat-modal-dialog" onClick={(e) => e.stopPropagation()}>
        {/* === Modal Header === */}
        <div className="cat-modal-header">
          <div className="header-left">
            <div className="header-title-badge-box">
              <div className="title-icon-ring">
                <FaFileInvoiceDollar size={16} color="#2563eb" />
              </div>
              <div>
                <h3 className="cat-modal-title">{displayTitle}</h3>
                <span className="record-count-subtitle">{filteredRecords.length} {filteredRecords.length === 1 ? 'Transaction' : 'Transactions'}</span>
              </div>
            </div>
          </div>

          <div className="header-right">
            <div className="kpi-stat-strip">
              <div className="kpi-mini-pill total-pill">
                <span className="kpi-mini-label">Total Amount</span>
                <span className="kpi-mini-val">₹ {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>

              <div className="kpi-mini-pill selected-pill">
                <span className="kpi-mini-label">Selected Amount</span>
                <span className={`kpi-mini-val ${selectedAmount > 0 ? "highlight-emerald" : ""}`}>
                  ₹ {selectedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <button
              className="btn-download-action"
              onClick={handleCSVDownload}
              title="Export Selected or Filtered CSV"
            >
              <FaDownload size={12} /> Export CSV
            </button>

            <button className="cat-modal-close-btn" onClick={onClose} title="Close Modal">
              <FaTimes size={15} />
            </button>
          </div>
        </div>

        {/* === Table Container === */}
        <div className="cat-modal-body">
          {loading ? (
            <div className="cat-status-msg">
              <div className="loading-spinner-ring"></div>
              <span>Loading category transactions...</span>
            </div>
          ) : errorMessage ? (
            <div className="cat-status-msg error">
              <FaExclamationTriangle size={24} color="#e11d48" />
              <span>{errorMessage}</span>
            </div>
          ) : (
            <table className="cat-records-table">
              <thead>
                <tr>
                  <th className="col-chk-hdr">
                    <button className="checkbox-toggle-btn" onClick={toggleSelectAll} title="Select All">
                      {isAllSelected ? <FaCheckSquare color="#2563eb" size={16} /> : <FaSquare color="#cbd5e1" size={16} />}
                    </button>
                  </th>

                  <th className="col-date-hdr">
                    <div className="hdr-cell-content">
                      <FaCalendarAlt size={12} color="#64748b" />
                      <span>DATE</span>
                    </div>
                  </th>

                  <th className="col-patient-hdr">
                    <div className="filter-hdr-container">
                      <div
                        className={`hdr-cell-content filterable-hdr ${patientFilter ? 'active-hdr-filter' : ''}`}
                        onClick={() => setShowPatientFilter((prev) => !prev)}
                      >
                        <FaUser size={12} color="#64748b" />
                        <span>PATIENT</span>
                        <FaFilter size={10} color={patientFilter ? "#2563eb" : "#94a3b8"} />
                      </div>
                      {showPatientFilter && (
                        <div className="filter-input-popover">
                          <input
                            type="text"
                            placeholder="Search patient/MR..."
                            value={patientFilter}
                            onChange={(e) => setPatientFilter(e.target.value)}
                            autoFocus
                          />
                          {patientFilter && (
                            <button className="clear-pill-btn" onClick={() => setPatientFilter('')}>Clear</button>
                          )}
                        </div>
                      )}
                    </div>
                  </th>

                  <th className="col-phone-hdr">
                    <div className="hdr-cell-content">
                      <FaPhoneAlt size={12} color="#64748b" />
                      <span>PHONE</span>
                    </div>
                  </th>

                  {showDoctorAndAddedBy && (
                    <th className="col-doctor-hdr">
                      <div className="hdr-cell-content">
                        <FaUserMd size={12} color="#64748b" />
                        <span>DOCTOR</span>
                      </div>
                    </th>
                  )}

                  {showDoctorAndAddedBy && (
                    <th className="col-addedby-hdr">
                      <div className="filter-hdr-container center-align">
                        <div
                          className={`hdr-cell-content filterable-hdr ${addedByFilter ? 'active-hdr-filter' : ''}`}
                          onClick={() => setShowAddedByFilter((prev) => !prev)}
                        >
                          <FaUserPlus size={12} color="#64748b" />
                          <span>ADDED BY</span>
                          <FaFilter size={10} color={addedByFilter ? "#2563eb" : "#94a3b8"} />
                        </div>
                        {showAddedByFilter && (
                          <div className="filter-input-popover">
                            <input
                              type="text"
                              placeholder="Filter staff..."
                              value={addedByFilter}
                              onChange={(e) => setAddedByFilter(e.target.value)}
                              autoFocus
                            />
                            {addedByFilter && (
                              <button className="clear-pill-btn" onClick={() => setAddedByFilter('')}>Clear</button>
                            )}
                          </div>
                        )}
                      </div>
                    </th>
                  )}

                  <th className="col-amount-hdr">
                    <div className="hdr-cell-content right-align">
                      <span>TOTAL AMOUNT (₹)</span>
                    </div>
                  </th>

                  <th className="col-amount-hdr">
                    <div className="hdr-cell-content right-align">
                      <span style={{ color: "#16a34a" }}>PAID AMOUNT (₹)</span>
                    </div>
                  </th>

                  <th className="col-status-hdr" style={{ textAlign: "center" }}>
                    <div className="hdr-cell-content center-align">
                      <FaCheckCircle size={12} color="#64748b" />
                      <span>STATUS</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.length > 0 ? (
                  filteredRecords.map((rec) => {
                    const isChecked = selectedRowIds.includes(rec.id);
                    const completed = isCompletedStatus(rec.status);
                    return (
                      <tr
                        key={rec.id}
                        className={`cat-record-row ${isChecked ? 'row-selected' : ''}`}
                        onClick={() => toggleRowSelect(rec.id)}
                      >
                        <td className="col-chk" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleRowSelect(rec.id)}
                          />
                        </td>
                        <td className="col-date">{rec.date}</td>
                        <td className="col-patient">
                          <div className="patient-name-block">
                            <span className="patient-main-name">{rec.patientName || 'N/A'}</span>
                            <span className="patient-mr-tag">{rec.mrNumber || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="col-phone">{rec.phone || 'N/A'}</td>

                        {showDoctorAndAddedBy && (
                          <td className="col-doctor">
                            <span className="doctor-name-text">{rec.doctorName || 'System'}</span>
                          </td>
                        )}

                        {showDoctorAndAddedBy && (
                          <td className="col-addedby text-center">
                            <span className="addedby-pill">{rec.addedBy || 'System'}</span>
                          </td>
                        )}

                        <td className="col-amount">
                          <span className="total-amt-text">
                            ₹ {(rec.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td className="col-amount">
                          <div className="amount-cell-content flex-column-cell">
                            <div className="paid-val-row">
                              <span className="paid-amt-text">
                                ₹ {((rec.paidAmount !== undefined && rec.paidAmount !== null) ? rec.paidAmount : (rec.amount || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                              {rec.appointmentId && (
                                <button
                                  className="view-appointment-link-btn"
                                  title={`Open Appointment Details (${rec.appointmentId}) in new window`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`/outpatient/appointments/patientInfoPanel/${rec.appointmentId}`, '_blank');
                                  }}
                                >
                                  <FaExternalLinkAlt size={11} />
                                </button>
                              )}
                            </div>
                            {rec.refundAmount > 0 && (
                              <span className="refund-inc-tag">
                                (₹ {rec.refundAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} refunded)
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="col-status text-center">
                          <span className={`status-pill-badge ${completed ? "status-completed" : "status-pending"}`}>
                            {completed ? "Completed" : "Pending"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={showDoctorAndAddedBy ? 9 : 7} className="empty-cat-msg">
                      No matching transaction records found in database.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryRecordsModal;
